import React from "react";
import { clsx } from "clsx";

import { SUPPORTED_THEMES } from "~constants";
import Headline from "~components/shared/headline/Headline";
import {
    type MediaConfig,
    Media,
    MediaFormat,
    getWinningMediaFormat,
} from "~components/shared/media/";
import PhoenixByline from "~components/shared/phoenix-byline/PhoenixByline";
import { VariantBase } from "~components/shared/variant-base/VariantBase";
import { HEADLINE_SIZE } from "~types/lineup";
import { type VariationComponent, ZoneColumnCombo } from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentFeatureLede.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentFeatureLede.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [
    ZoneColumnCombo.fullWidthContentWell,
];

const VERTICAL_MEDIA_FORMATS = new Set<MediaFormat>([
    MediaFormat.verticalVideo,
    MediaFormat.loopingVideo,
]);

const IMAGE_MEDIA_FORMATS = new Set<MediaFormat>([
    MediaFormat.magazineCoverImage,
    MediaFormat.defaultImage,
]);

export const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.verticalVideo,
    MediaFormat.loopingVideo,
    MediaFormat.vodVideo,
    MediaFormat.embeddedIframe,
    MediaFormat.defaultImage,
];

const MEDIA_CONFIG: MediaConfig = {
    className: styles.media,
    mobileMaxWidth: 715,
};

const DEFAULT_ASPECT_RATIOS: Record<string, number> = {
    landscape: 3 / 2,
    portrait: 2 / 3,
};

export type LineupContentFeatureLedeProps = VariationComponent & {
    useCoverImage?: boolean;
};

const LineupContentFeatureLede = (props: LineupContentFeatureLedeProps) => {
    const integrity = integrityCheck(props);

    if (integrity.length) return integrityError(integrity);

    // useCoverImage is a prop set explicitly when this
    // module is used on Magazine Index pages, it is NOT
    // a prop from curation.
    const { items, theme, useCoverImage = false } = props;
    const [item] = items;
    const { media } = item;

    const chosenMediaFormatForItem =
        useCoverImage && media?.magazineCoverImage
            ? MediaFormat.magazineCoverImage
            : getWinningMediaFormat(item, MEDIA_PRIORITY);

    if (chosenMediaFormatForItem === MediaFormat.missingMedia) return null;

    let isLandscape = true;
    let aspectRatio = DEFAULT_ASPECT_RATIOS.landscape;

    if (IMAGE_MEDIA_FORMATS.has(chosenMediaFormatForItem)) {
        const itemImage = useCoverImage
            ? media?.magazineCoverImage
            : media?.defaultImage;

        if (!itemImage) return null;

        const { origWidth = 3, origHeight = 2 } = itemImage;

        aspectRatio = origWidth / origHeight;

        if (aspectRatio <= 1) {
            isLandscape = false;
        }
    } else if (VERTICAL_MEDIA_FORMATS.has(chosenMediaFormatForItem)) {
        isLandscape = false;
        aspectRatio = DEFAULT_ASPECT_RATIOS.portrait;
    }

    const updatedMediaConfig: MediaConfig = {
        ...MEDIA_CONFIG,
        aspectRatio,
        desktopMaxWidth: isLandscape ? 830 : 565,
    };

    return (
        <VariantBase
            source={props}
            className={clsx([
                styles.LineupContentFeatureLede,
                {
                    [styles.businessweek]:
                        theme === SUPPORTED_THEMES.businessweek,
                },
                { [styles.landscape]: isLandscape },
                { [styles.portrait]: !isLandscape },
            ])}
            gridClassName={styles.grid}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <Media
                source={item}
                priority={[chosenMediaFormatForItem]}
                config={updatedMediaConfig}
            />
            <div className={styles.storyBlock} data-component="story-block">
                <a
                    href={item.url}
                    className={styles.storyLink}
                    data-link-index={1}
                >
                    <Headline
                        text={item.headline}
                        isPhoenix
                        className={styles.headline}
                        size={HEADLINE_SIZE.EXTRA_EXTRA_LARGE}
                    />
                    <PhoenixByline forceShowByline story={item} />
                </a>
            </div>
        </VariantBase>
    );
};

export default LineupContentFeatureLede;
